import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useGuardedNavigate } from "../../../shared/leaveGuard/LeaveGuardProvider";
import styled from "styled-components";
import { flattenSidebarDestinations } from "../../navigation/navigationCatalog";
import { useNavigationLayout } from "../../../../hooks/useNavigationLayout";
import { trackEvent } from "../../../../helper/trackEvent";
import { useGetGlobalSearchQuery } from "../../../../store/apiSlice/globalSearchApiSlice";
import {
  MIN_RECORD_QUERY,
  readRecentRecords,
  rememberRecentRecord,
  toRecordRow,
} from "./recordSearch";

const SEARCH_DELAY_MS = 250;

/**
 * Global quick navigation dialog (Ctrl/Cmd+K): menu pages plus records such as
 * sales orders, shipments and customers.
 */
export function QuickJump({ isOpen, onClose }) {
  return isOpen ? <QuickJumpDialog isOpen onClose={onClose} /> : null;
}

function QuickJumpDialog({ isOpen, onClose }) {
  const navigate = useGuardedNavigate();
  const { items: visibleModules } = useNavigationLayout("sidebar");
  const { userId, companyId } =
    useSelector(
      state => ({
        userId: state?.auth?.user?.id,
        companyId: state?.auth?.user?.company_id ?? state?.auth?.user?.company?.id,
      }),
      shallowEqual,
    ) || {};
  const [query, setQuery] = useState("");
  const [recordQuery, setRecordQuery] = useState("");
  const [recent, setRecent] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const destinations = useMemo(() => flattenSidebarDestinations(visibleModules), [visibleModules]);

  const trimmed = query.trim();
  const wantsRecords = trimmed.length >= MIN_RECORD_QUERY;

  useEffect(() => {
    if (!wantsRecords) {
      setRecordQuery("");
      return undefined;
    }
    const timer = window.setTimeout(() => setRecordQuery(trimmed), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed, wantsRecords]);

  const { data: hits = [], isFetching: searching } = useGetGlobalSearchQuery(recordQuery, {
    skip: !isOpen || !recordQuery,
  });

  const pages = useMemo(() => {
    const q = trimmed.toLowerCase();
    const rows = q
      ? destinations.filter(row => {
          const haystack = `${row.name} ${row.moduleLabel} ${row.group ?? ""}`.toLowerCase();
          return haystack.includes(q);
        })
      : destinations.slice(0, 12);
    return rows.map(row => ({
      key: row.childKey,
      title: row.name,
      meta: row.moduleLabel,
      url: row.url,
    }));
  }, [destinations, trimmed]);

  const records = useMemo(() => {
    if (!trimmed) return recent;
    if (!wantsRecords || recordQuery !== trimmed) return [];
    return hits.map(toRecordRow).filter(Boolean);
  }, [hits, recent, recordQuery, trimmed, wantsRecords]);

  const recordsPending = wantsRecords && (recordQuery !== trimmed || searching);

  // Exact record matches (an order number) are what people want most.
  const groups = useMemo(() => {
    const recordGroup = { label: trimmed ? "Records" : "Recent", rows: records, isRecord: true };
    const pageGroup = { label: "Pages", rows: pages, isRecord: false };
    return (trimmed ? [recordGroup, pageGroup] : [pageGroup, recordGroup]).filter(
      group => group.rows.length,
    );
  }, [pages, records, trimmed]);

  const filtered = useMemo(
    () => groups.flatMap(group => group.rows.map(row => ({ ...row, isRecord: group.isRecord }))),
    [groups],
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setQuery("");
    setHighlightIndex(0);
    setRecent(readRecentRecords(userId, companyId));
    trackEvent("quick_jump_opened");
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, userId, companyId]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightIndex(i => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightIndex(i => Math.max(i - 1, 0));
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [filtered.length, isOpen, onClose]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-highlighted="true"]');
    el?.scrollIntoView?.({ block: "nearest" });
  }, [highlightIndex, filtered]);

  if (!isOpen) {
    return null;
  }

  const goTo = row => {
    if (row.isRecord) {
      const { isRecord, ...stored } = row;
      rememberRecentRecord(userId, companyId, stored);
      trackEvent("quick_jump_record_opened", { record_type: row.type });
    }
    navigate(row.url);
    onClose();
  };

  const onSubmit = event => {
    event.preventDefault();
    const target = filtered[highlightIndex];
    if (target) {
      goTo(target);
    }
  };

  let rowIndex = -1;

  return (
    <Backdrop role="presentation" onMouseDown={onClose}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-label="Jump to page"
        onMouseDown={event => event.stopPropagation()}
      >
        <form onSubmit={onSubmit}>
          <SearchInput
            ref={inputRef}
            type="search"
            placeholder="Search pages, orders, customers…"
            value={query}
            onChange={event => setQuery(event.target.value)}
            aria-autocomplete="list"
            aria-controls="quick-jump-list"
          />
        </form>
        <ResultList id="quick-jump-list" ref={listRef} role="listbox">
          {groups.map(group => (
            <div key={group.label} role="group" aria-label={group.label}>
              <GroupLabel>{group.label}</GroupLabel>
              {group.rows.map(row => {
                rowIndex += 1;
                const index = rowIndex;
                return (
                  <ResultRow
                    key={row.key}
                    type="button"
                    role="option"
                    data-highlighted={index === highlightIndex ? "true" : "false"}
                    aria-selected={index === highlightIndex}
                    $active={index === highlightIndex}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => goTo({ ...row, isRecord: group.isRecord })}
                  >
                    <span className="title">
                      {row.title}
                      {row.subtitle ? <span className="subtitle"> · {row.subtitle}</span> : null}
                    </span>
                    <span className="meta">{row.meta}</span>
                  </ResultRow>
                );
              })}
            </div>
          ))}
          {recordsPending ? <Searching role="status">Searching records…</Searching> : null}
          {!recordsPending && filtered.length === 0 ? (
            <EmptyState>
              {trimmed ? `No matching pages or records for "${trimmed}"` : "No matching pages"}
            </EmptyState>
          ) : null}
        </ResultList>
        <HintRow>
          <span>↑↓ navigate</span>
          <span>Enter open</span>
          <span>Esc close</span>
        </HintRow>
      </Dialog>
    </Backdrop>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 600;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
`;

const Dialog = styled.div`
  width: min(480px, calc(100vw - 32px));
  background: #fff;
  border-radius: 8px;
  border: 1px solid #d5deea;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.18);
  overflow: hidden;
  font-family: Inter, sans-serif;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid #e9edf5;
  padding: 16px 18px;
  font-size: 16px;
  color: #0e0e2c;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }
`;

const ResultList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;
`;

const ResultRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: none;
  border-radius: 6px;
  padding: 10px 12px;
  background: ${({ $active }) => ($active ? "#e8effd" : "transparent")};
  cursor: pointer;
  text-align: left;

  .title {
    color: ${({ $active }) => ($active ? "#2060ea" : "#262641")};
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 600 : 500)};
  }

  .subtitle {
    color: #6b7280;
    font-weight: 400;
  }

  .meta {
    color: #6b7280;
    font-size: 12px;
    flex-shrink: 0;
  }

  &:hover {
    background: #f1f5fd;
  }
`;

const GroupLabel = styled.div`
  padding: 8px 12px 4px;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Searching = styled.div`
  padding: 8px 12px;
  color: #6b7280;
  font-size: 13px;
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const HintRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 16px 12px;
  border-top: 1px solid #e9edf5;
  color: #9ca3af;
  font-size: 11px;
`;
